import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Group,
  Modal,
  Progress,
  Skeleton,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCircleCheck,
  IconDeviceFloppy,
  IconDownload,
  IconEraser,
  IconFilePlus,
  IconPaperclip,
  IconPencil,
  IconPrinter,
  IconRefresh,
  IconSend,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { useShell } from '../../shell-context.js';
import docs from '../documents/Documents.module.css';
import { FileViewer, type FileViewerHandle } from '../documents/FileViewer.js';
import { revisionText } from '../documents/library.js';
import type { DocumentSource } from '../documents/source.js';
import { ApprovalPanel } from '../pms/ApprovalPanel.js';
import { PmsPasswordDialog } from '../pms/PmsWorkParts.js';
import { AmendDrawer, type AmendInput } from './AmendDrawer.js';
import { FormRecordHeader } from './FormRecordHeader.js';
import {
  ApprovalSteps,
  DeliveryMark,
  Fact,
  OfficeFileBadge,
  ReasonDialog,
  RecordBadges,
  RecordFileList,
  ShipTransferMark,
  WarningAlert,
} from './FormRecordParts.js';
import classes from './Forms.module.css';
import { PdfFormFiller } from './PdfFormFiller.js';
import { attachFilledPdf, recordFileName } from './pdf-form.js';
import { RecordGuide } from './RecordGuide.js';
import { RevisionList } from './RevisionList.js';
import { type DraftValues, draftSchema } from './record-forms.js';
import {
  appendFiles,
  fileKindOf,
  lastOfficeFile,
  moveFile,
  recordPath,
  removeFile,
  sortedFiles,
  uploadNeedsConfirm,
} from './record-model.js';
import { enteredBy, recordCoverFileName, recordCoverPdf } from './record-pdf.js';
import type { FormRecordSource, RecordUploadProgress } from './source.js';
import {
  flagLabel,
  formErrorText,
  recordStatusBadges,
  recordTime,
  rejectedFilesText,
  uploadProgressPercent,
  uploadProgressText,
} from './status.js';
import type { DraftFile, FormRecordDetail, FormTemplate, OfficeFileState } from './types.js';

export interface FormRecordScreenProps {
  recordId: string;
  source: FormRecordSource;
  /**
   * Gemide doküman kütüphanesi: Open Form'un şablon dosyası ve Fill in app'in PDF'i revizyonun
   * dosyasından (ADR 0042 §18, §19). Ofiste yok.
   */
  documents?: DocumentSource | undefined;
  /** Başlıktaki ek eylemler (kapak sayfası; Task 20). */
  headerActions?: (record: FormRecordDetail) => ReactNode;
  /** Kayıt dosyasının baskı altlığı (kayıt no, AMENDED; ADR 0028 #30, ADR 0042 §20). */
  printFooter?: (record: FormRecordDetail, printedRevisionNo: number | null) => string;
  /**
   * Önceden okunmuş kayıt ve şablon (birim testi); verilmezse ekran kendisi okur. `officeOpen`:
   * form Felixmate Office Pack'te açık (ekran bunu ana süreçten sorar).
   */
  preload?: { record: FormRecordDetail; template?: FormTemplate | null; officeOpen?: boolean };
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; record: FormRecordDetail };

interface Preview {
  sha256: string;
  name: string;
  size: number;
  mimeType: string;
  id?: string;
  revisionNo: number | null;
}

type Dialog =
  | null
  | 'submit'
  | 'discard'
  | 'void'
  | 'resubmit'
  | 'amend'
  | 'fill'
  | 'stillOpen'
  | 'confirmUpload';

const errorCodeOf = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : 'GENERIC';

/** Önizlenecek ilk dosya: taslakta taslağın, gönderilmişte son revizyonun ilk dosyası. */
function firstPreview(record: FormRecordDetail): Preview | null {
  const files: readonly (DraftFile & { id?: string })[] = record.draft?.files ?? record.files;
  const first = sortedFiles(files)[0];
  return first
    ? {
        sha256: first.sha256,
        name: first.name,
        size: first.size,
        mimeType: first.mimeType,
        ...(first.id ? { id: first.id } : {}),
        revisionNo: record.status.revisionNo,
      }
    : null;
}

/**
 * Kayıt sayfası `/compliance/ism-forms/records/:id` (ADR 0042 §21; plan M8b Task 18): menüde
 * yoktur, sayfa içi bağlantıyla açılır. Düzen gemi-ism-formlari.jpeg dilindedir: başlık kartı
 * (kayıt no, form, durum rozetleri), solda dosyalar ve önizleme, sağda Record Information ve
 * Actions, altta revizyonlar ve onaylar; taslakta altta "Save Draft" ve birincil "Submit"
 * (menu-yapisi.md). Gönderim, değişiklik, yeniden gönderim, karar ve iptal parola ister (§12).
 * Ofiste kayıt salt okunurdur; yalnız ofis adımına karar verilir (gereksinim §12).
 */
export function FormRecordScreen({
  recordId,
  source,
  documents,
  headerActions,
  printFooter,
  preload,
}: FormRecordScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const ship = source.ship;
  const work = ship?.work;
  const timeZone = source.printer.timeZone;
  const [state, setState] = useState<State>(
    preload ? { kind: 'ready', record: preload.record } : { kind: 'loading' },
  );
  const [preview, setPreview] = useState<Preview | null>(
    preload ? firstPreview(preload.record) : null,
  );
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<{
    tone: 'danger' | 'success' | 'warning';
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Kayıt dosyası yüklemesinin ilerlemesi (ADR 0055 §4) [sorular #343]
  const [uploading, setUploading] = useState<RecordUploadProgress | null>(null);
  const [template, setTemplate] = useState<FormTemplate | null>(preload?.template ?? null);
  const preloaded = preload !== undefined;
  const [templateFile, setTemplateFile] = useState<{ id: string; name: string } | null>(null);
  const viewer = useRef<FileViewerHandle>(null);
  const record = state.kind === 'ready' ? state.record : null;
  // Form Felixmate Office Pack'te açık mı (sahip kararı 2026-10-17; ADR 0074): belirgin şerit, yükleme
  // kapalı. Ana süreç izlediği kaydı söyler; Fill in'den sonra ve kapanış olayında güncellenir
  const [officeOpen, setOfficeOpen] = useState(preload?.officeOpen ?? false);
  useEffect(() => {
    if (preloaded || !work?.editing) return;
    let live = true;
    void work.editing(recordId).then(
      (editing) => live && setOfficeOpen(editing),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [preloaded, work, recordId]);

  const load = useCallback(async () => {
    try {
      const detail = await source.detail(recordId);
      setState({ kind: 'ready', record: detail });
      setPreview((current) =>
        current &&
        [...(detail.draft?.files ?? []), ...detail.revisions.flatMap((r) => r.files)].some(
          (f) => f.sha256 === current.sha256,
        )
          ? current
          : firstPreview(detail),
      );
    } catch (error) {
      setState({ kind: 'error', code: errorCodeOf(error) });
    }
  }, [source, recordId]);

  useEffect(() => {
    if (preloaded) return;
    setState({ kind: 'loading' });
    setPreview(null);
    void load();
  }, [load, preloaded]);

  // Öteki PC'nin gönderimi ya da kararı (SSE `records`), ofis kararı inince (senkron) tazelenir
  useEffect(() => source.subscribe?.(() => void load()), [source, load]);

  // Felixmate Office Pack'te kapanıştan sonra ana süreç Office dosyasını ve PDF kopyasını taslağa
  // koymuştur (ADR 0070 §8): kayıt yeniden okunur, sonuç bildirilir
  useEffect(
    () =>
      work?.onAutoAttach?.((event) => {
        if (event.recordId !== recordId) return;
        setOfficeOpen(false);
        void load();
        const notices = {
          attached: { tone: 'success', key: 'forms.record.autoAttached' },
          attached_without_pdf: { tone: 'warning', key: 'forms.record.autoAttachedWithoutPdf' },
          not_saved: { tone: 'warning', key: 'forms.record.autoNotSaved' },
          not_opened: { tone: 'warning', key: 'forms.record.autoNotOpened' },
          failed: { tone: 'danger', key: 'forms.record.autoFailed' },
        } as const;
        const notice = notices[event.outcome];
        setNotice({ tone: notice.tone, text: t(notice.key) });
      }),
    [work, recordId, load, t],
  );

  // Taslağın şablonu (gemide): yürürlükten kalktıysa uyarı ve "yeni revizyonla aç" (§3)
  const draftRevision = record?.status.status === 'draft' ? record.revisionId : null;
  useEffect(() => {
    if (preloaded) return;
    if (!ship || !record || !draftRevision) {
      setTemplate(null);
      return;
    }
    let cancelled = false;
    void ship
      .template(record.revisionSource, draftRevision)
      .then((info) => !cancelled && setTemplate(info))
      .catch(() => !cancelled && setTemplate(null));
    return () => {
      cancelled = true;
    };
  }, [preloaded, ship, record, draftRevision]);

  // Open Form ve Fill in app için revizyonun dosya kimliği (doküman dosyası ucu)
  useEffect(() => {
    if (!documents || !record || record.status.status !== 'draft') {
      setTemplateFile(null);
      return;
    }
    let cancelled = false;
    void documents
      .detail(record.documentId)
      .then((detail) => {
        if (cancelled) return;
        const revision = detail.revisions.find((r) => r.id === record.revisionId);
        setTemplateFile(revision ? { id: revision.file.id, name: revision.file.name } : null);
      })
      .catch(() => !cancelled && setTemplateFile(null));
    return () => {
      cancelled = true;
    };
  }, [documents, record]);

  const fail = (error: unknown) => setNotice({ tone: 'danger', text: formErrorText(t, error) });

  /** Bir işlem: meşgul işareti, hata bildirimi, sonra kayıt yeniden okunur. */
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setNotice(null);
    try {
      await fn();
      await load();
      return true;
    } catch (error) {
      fail(error);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const draftForm = useForm<DraftValues>({
    resolver: zodResolver(draftSchema),
    defaultValues: { title: '', note: '' },
  });
  // Taslak satırı değişince (satır sürümü) form sunucudaki değerlere döner
  const draftKey = record?.draft ? `${record.id}:${record.draft.rowVersion}` : null;
  const draftTitle = record?.draft?.title ?? '';
  const draftNote = record?.draft?.note ?? '';
  useEffect(() => {
    if (draftKey) draftForm.reset({ title: draftTitle, note: draftNote });
  }, [draftKey, draftTitle, draftNote, draftForm]);

  const saveFiles = (files: DraftFile[]) =>
    record?.draft && ship
      ? run('files', () =>
          ship.updateDraft(record.id, { expectedRowVersion: record.draft?.rowVersion ?? 0, files }),
        )
      : Promise.resolve(false);

  if (state.kind === 'loading') {
    return (
      <div className={classes.recordScreen}>
        <PageHeader title={t('forms.record.pageTitle')} titleHidden />
        <div className={classes.recordSkeleton} aria-busy="true">
          <span className={classes.srOnly}>{t('forms.record.loading')}</span>
          <Skeleton height={96} radius="md" />
          <Skeleton height={420} radius="md" mt={12} />
        </div>
      </div>
    );
  }
  if (state.kind === 'error' || !record) {
    const code = state.kind === 'error' ? state.code : 'GENERIC';
    return (
      <div className={classes.recordScreen}>
        <PageHeader title={t('forms.record.pageTitle')} titleHidden />
        <Panel>
          <EmptyState
            tone="danger"
            title={formErrorText(t, { code })}
            action={
              <Button
                variant="default"
                leftSection={<IconRefresh size={16} />}
                onClick={() => {
                  setState({ kind: 'loading' });
                  void load();
                }}
              >
                {t('forms.record.retry')}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  const status = record.status.status;
  const isDraft = status === 'draft';
  const submitted = status === 'pending' || status === 'returned' || status === 'approved';
  const can = record.can;
  const draftFiles = record.draft?.files ?? [];
  const office = source.scope === 'web';
  const officeFile = (sha: string) => record.files.find((f) => f.sha256 === sha);
  const previewAllowed = (() => {
    if (!preview) return false;
    if (!office) return true;
    // Ofiste dosya bulutta doğrulanmışsa ve dışa aktarma yetkisiyle (ADR 0042 Ek U28, U30)
    const file = officeFile(preview.sha256) ?? null;
    const revisionFile = record.revisions
      .flatMap((r) => r.files)
      .find((f) => f.sha256 === preview.sha256);
    return can.export && (file ? file.officeState === 'verified' : Boolean(revisionFile));
  })();

  const openInOffice = async () => {
    if (!work || !record.draft) return;
    const last = lastOfficeFile(draftFiles);
    if (!last && !templateFile) {
      fail({ code: 'FILE_NOT_ON_BOARD' });
      return;
    }
    setBusy('open');
    setNotice(null);
    try {
      const result = await work.openInOffice({
        recordId: record.id,
        file: last
          ? { kind: 'record', sha256: last.sha256, name: last.name }
          : {
              kind: 'template',
              fileId: templateFile?.id ?? '',
              sha256: template?.source.sha256 ?? '',
              name: recordFileName(record.recordNo, templateFile?.name ?? 'form.docx'),
            },
      });
      if (result.ok) {
        if (result.via === 'office-pack') {
          // Şerit Office açıkken durumu söyler; ayrıca bildirim gerekmez
          setOfficeOpen(true);
          return;
        }
        setNotice({
          tone: 'success',
          text: t(
            result.via === 'office-pack'
              ? 'forms.record.openedInOfficePack'
              : 'forms.record.openedInOffice',
          ),
        });
      } else if (result.code === 'NO_ASSOCIATED_APP') {
        setNotice({ tone: 'warning', text: t('forms.errors.NO_ASSOCIATED_APP') });
      } else fail(result);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const attach = async (force = false) => {
    if (!work || !record.draft) return;
    setBusy('attach');
    setNotice(null);
    try {
      const result = await work.attach({
        recordId: record.id,
        templateSha256: template?.source.sha256 ?? '',
        ...(force ? { force: true } : {}),
      });
      if (!result.ok) {
        if (result.code === 'FILE_STILL_OPEN') setDialog('stillOpen');
        else fail(result);
        return;
      }
      // Office dosyası ve PDF kopyası kayıtta tektir: yeni kaydedilen kopya öncekinin yerine geçer;
      // PDF yoksa eski PDF kopyası da çıkar (§18; Ek U50; ADR 0070 §6)
      const others = draftFiles.filter((f) => f.source !== 'office' && f.source !== 'office_pdf');
      const files = appendFiles(others, [
        { file: result.file, source: 'office' },
        ...(result.pdf ? [{ file: result.pdf, source: 'office_pdf' as const }] : []),
      ]);
      const saved = await saveFiles(files);
      if (saved && 'warnings' in result && result.warnings.includes('UNCHANGED_TEMPLATE')) {
        setNotice({ tone: 'warning', text: t('forms.errors.UNCHANGED_TEMPLATE') });
      } else if (saved && 'warnings' in result && result.warnings.includes('PDF_FAILED')) {
        setNotice({ tone: 'warning', text: t('forms.errors.PDF_FAILED') });
      } else if (saved) {
        setNotice({ tone: 'success', text: t('forms.record.attached') });
      }
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const pick = async (fileSource: 'scan' | 'photo' | 'file') => {
    if (!work) return;
    // "Upload filled file" yönlendirmedeki düğmedir (`upload`)
    setBusy(fileSource === 'file' ? 'upload' : fileSource);
    setNotice(null);
    try {
      const stop = work.onUploadProgress?.(setUploading);
      const picked = await work
        .pickFiles({
          title: t(
            fileSource === 'file'
              ? 'forms.guide.upload'
              : fileSource === 'scan'
                ? 'forms.files.addScan'
                : 'forms.files.addPhoto',
          ),
          source: fileSource,
        })
        .finally(() => stop?.());
      setUploading(null);
      // Reddedilen dosyalar adıyla; yüklenenler yine kayda girer (ADR 0055 §3) [sorular #342]
      const rejected = rejectedFilesText(t, picked.failed);
      if (picked.files.length === 0) {
        if (rejected) setNotice({ tone: 'danger', text: rejected });
        return;
      }
      const saved = await saveFiles(appendFiles(draftFiles, picked.files));
      if (saved && rejected) {
        setNotice({ tone: 'warning', text: rejected });
      } else if (saved && picked.files.some((p) => p.file.warnings.includes('LOW_RESOLUTION'))) {
        // Düşük çözünürlük uyarısı (ret yok; ADR 0042 §6) [sorular #231]
        setNotice({ tone: 'warning', text: t('forms.errors.LOW_RESOLUTION') });
      }
    } catch (error) {
      fail(error);
    } finally {
      setUploading(null);
      setBusy(null);
    }
  };

  const decide = async (input: {
    decision: 'approved' | 'returned';
    reason?: string;
    password?: string;
    submissionId: string;
    step: number;
    contentHash: string;
  }) => {
    const body = {
      decision: input.decision,
      ...(input.reason ? { reason: input.reason } : {}),
      // ISM formunda parola isteğe bağlı (ADR 0056): yalnız girildiyse
      ...(input.password ? { password: input.password } : {}),
      expected: {
        submissionId: input.submissionId,
        step: input.step,
        contentHash: input.contentHash,
      },
    };
    const detail = office
      ? await source.office?.decide(record.id, body)
      : await ship?.decide(record.id, body);
    if (detail) setState({ kind: 'ready', record: detail });
    setNotice({
      tone: 'success',
      text: t(input.decision === 'approved' ? 'forms.record.approved' : 'forms.record.returned'),
    });
  };

  const reopenWithCurrent = async () => {
    if (!ship) return;
    setBusy('reopen');
    try {
      const opened = await ship.open({
        documentId: record.documentId,
        revisionSource: record.revisionSource,
        fillMethod: record.fillMethod,
        title: record.draft?.title ?? record.title,
      });
      shell?.navigate(recordPath(opened.id));
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const pdfFillUrl = (() => {
    const last = sortedFiles(draftFiles)
      .filter((f) => f.source === 'pdf_form')
      .at(-1);
    if (last) return source.fileUrl(record, last, 'view');
    return templateFile && documents ? documents.fileUrl(templateFile.id, 'preview') : null;
  })();

  /**
   * Kapak sayfası (ADR 0042 §20): dışa aktarma yetkisiyle basılır ya da indirilir; gemide gemi
   * saati, ofiste UTC; yazılım adı ve sürümü her sayfada [NR496 4.1.2].
   */
  const cover = async (kind: 'print' | 'save') => {
    setBusy(`cover-${kind}`);
    try {
      const render = async () => {
        const { renderPdf } = await import('../personnel/pdf.js');
        return renderPdf(
          recordCoverPdf((key, values) => t(key, values ?? {}), record, source),
          {
            print: kind === 'print',
          },
        );
      };
      await source.pdf[kind](recordCoverFileName(record), render);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };
  const coverActions =
    can.export && status !== 'draft' && status !== 'discarded' ? (
      <>
        <Button
          variant="default"
          className={docs.headButton}
          leftSection={<IconDownload size={16} />}
          loading={busy === 'cover-save'}
          onClick={() => void cover('save')}
        >
          {t('forms.cover.download')}
        </Button>
        <Button
          variant="default"
          className={docs.headButton}
          leftSection={<IconPrinter size={16} />}
          loading={busy === 'cover-print'}
          onClick={() => void cover('print')}
        >
          {t('forms.cover.print')}
        </Button>
      </>
    ) : null;

  const superseded = isDraft && template !== null && !template.current;
  // Son iade kararı (ADR 0116 §1): yalnız kayıt şimdi iade durumundaysa
  const lastReturn =
    status === 'returned'
      ? [...record.approvals].reverse().find((decision) => decision.decision === 'returned')
      : undefined;
  const returnedBy = lastReturn
    ? {
        by:
          [record.roles?.[lastReturn.approverRoleId], record.names[lastReturn.approverId]]
            .filter(Boolean)
            .join(' ') || '—',
        at: lastReturn.at,
        reason: lastReturn.reason,
        office: lastReturn.office,
      }
    : null;
  const badges = recordStatusBadges(t, record.status, record.flags);
  // "Hepsi" adımında imzası beklenen bütün rütbeler (ADR 0071 §4)
  const pendingRole = record.pending
    ? (record.pending.approverRoleIds ?? [record.pending.approverRoleId])
        .map((id) => record.roles?.[id] ?? '—')
        .join(' / ')
    : null;
  // Sırası gelmemiş onaycı (ADR 0069): Approve ve Return pasif, bekleyen rol yazılı
  const waiting =
    status === 'pending' && !can.decide && can.upcomingStep != null && record.pending !== null;

  const fileStatus = (file: { sha256: string }) => {
    const shown = record.files.find((f) => f.sha256 === file.sha256);
    if (!shown) return null;
    if (office && shown.officeState) {
      const progress =
        shown.officeState === 'uploading' && shown.bytesReceived !== undefined && shown.size > 0
          ? (100 * shown.bytesReceived) / shown.size
          : null;
      return (
        <OfficeFileBadge
          state={shown.officeState as OfficeFileState}
          progress={progress}
          waitingWindow={record.uploadState === 'window'}
        />
      );
    }
    return <ShipTransferMark transfer={shown.transfer} />;
  };

  const actionButton = (
    key: string,
    label: string,
    icon: ReactNode,
    onClick: () => void,
    options: { primary?: boolean; danger?: boolean; disabled?: string | null } = {},
  ) => {
    const button = (
      <Button
        key={key}
        fullWidth
        variant={options.primary ? 'filled' : options.danger ? 'subtle' : 'default'}
        {...(options.danger ? { color: 'red' } : {})}
        className={docs.actionButton}
        leftSection={icon}
        loading={busy === key}
        disabled={Boolean(options.disabled) || (busy !== null && busy !== key)}
        onClick={onClick}
      >
        {label}
      </Button>
    );
    return options.disabled ? (
      <Tooltip key={key} label={formErrorText(t, { code: options.disabled })} withArrow>
        <span className={docs.block}>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  const actions: ReactNode[] = [];
  // Doldurma, ekleme ve yükleme kayıt akışındadır (RecordGuide; ADR 0074): Actions'ta tekrar yok
  if (!office && isDraft && can.discard) {
    actions.push(
      actionButton(
        'discard',
        t('forms.record.discard'),
        <IconTrash size={16} />,
        () => setDialog('discard'),
        { danger: true },
      ),
    );
  }
  if (!office && submitted && can.amend) {
    actions.push(
      actionButton(
        'amend',
        t('forms.record.amend'),
        <IconPencil size={16} />,
        () => setDialog('amend'),
        { primary: true },
      ),
    );
  }
  if (!office && status === 'returned' && can.resubmit) {
    actions.push(
      actionButton('resubmit', t('forms.record.resubmit'), <IconSend size={16} />, () =>
        setDialog('resubmit'),
      ),
    );
  }
  if (preview && can.export && previewAllowed) {
    actions.push(
      actionButton(
        'download',
        t('forms.record.download'),
        <IconDownload size={16} />,
        () =>
          void source
            .downloadFile(record, {
              ...(preview.id ? { id: preview.id } : {}),
              sha256: preview.sha256,
              name: preview.name,
            })
            .catch(fail),
      ),
    );
  }
  if (!office && submitted && can.void) {
    actions.push(
      actionButton(
        'void',
        t('forms.record.void'),
        <IconEraser size={16} />,
        () => setDialog('void'),
        { danger: true },
      ),
    );
  }

  const facts = (
    <dl className={docs.sideFacts}>
      <Fact label={t('forms.info.recordNo')}>{record.recordNo}</Fact>
      {record.vessel ? <Fact label={t('forms.info.vessel')}>{record.vessel.name}</Fact> : null}
      <Fact label={t('forms.info.form')} title={record.documentTitle}>
        {t('forms.record.formNo', { no: record.docNo })} - {record.documentTitle}
      </Fact>
      <Fact label={t('forms.info.revision')}>
        {revisionText(record.revisionNo)} · {t(`forms.info.sources.${record.revisionSource}`)}
      </Fact>
      <Fact label={t('forms.info.fillMethod')}>{t(`forms.fillMethods.${record.fillMethod}`)}</Fact>
      <Fact label={t('forms.info.recordDay')}>{formatDay(record.recordDay)}</Fact>
      <Fact label={t('forms.info.enteredBy')}>{enteredBy(record)}</Fact>
      <Fact label={t('forms.info.createdAt')}>{recordTime(record.createdAt, timeZone)}</Fact>
      <Fact label={t('forms.info.status')}>
        <RecordBadges badges={badges.slice(0, 1)} />
      </Fact>
      {pendingRole ? (
        <Fact label={t('forms.info.awaiting')}>
          {t('forms.info.awaitingValue', {
            role: pendingRole,
            since: recordTime(record.pending?.since, timeZone),
          })}
        </Fact>
      ) : null}
      <Fact label={t('forms.info.delivery')}>
        <DeliveryMark delivery={record.delivery} />
      </Fact>
      {record.flags.length > 0 ? (
        <Fact label={t('forms.info.flags')}>
          {record.flags.map((flag) => flagLabel(t, flag)).join(' · ')}
        </Fact>
      ) : null}
      {record.paperRevisionReason ? (
        <Fact label={t('forms.info.paperReason')}>{record.paperRevisionReason}</Fact>
      ) : null}
      {!isDraft && record.title ? (
        <Fact label={t('forms.record.title')}>{record.title}</Fact>
      ) : null}
    </dl>
  );

  const viewerFile = preview
    ? {
        id: preview.id ?? preview.sha256,
        name: preview.name,
        size: preview.size,
        kind: fileKindOf(preview),
      }
    : null;

  const files = isDraft ? draftFiles : record.files;

  return (
    <div className={classes.recordScreen} data-scope={source.scope} data-status={status}>
      <PageHeader title={t('forms.record.pageTitle')} titleHidden crumb={record.recordNo} />
      {notice?.tone === 'warning' ? (
        <WarningAlert
          icon={<IconAlertTriangle size={18} />}
          withCloseButton
          closeButtonLabel={t('forms.dialog.dismiss')}
          onClose={() => setNotice(null)}
          className={classes.notice}
          role="status"
        >
          {notice.text}
        </WarningAlert>
      ) : notice ? (
        <Alert
          color={notice.tone === 'danger' ? 'red' : 'green'}
          icon={
            notice.tone === 'success' ? (
              <IconCircleCheck size={18} />
            ) : (
              <IconAlertTriangle size={18} />
            )
          }
          withCloseButton
          closeButtonLabel={t('forms.dialog.dismiss')}
          onClose={() => setNotice(null)}
          className={classes.notice}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </Alert>
      ) : null}
      {status === 'inconsistent' ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} className={classes.notice}>
          {t('forms.errors.INVALID_RECORD_STATE')}
        </Alert>
      ) : null}
      {returnedBy ? (
        // İade edilen kayıtta üstte bant (ortak 2026-10-22; ADR 0116 §1): iade eden, zaman, gerekçe
        <WarningAlert
          icon={<IconArrowBackUp size={18} />}
          className={classes.notice}
          title={t('forms.record.returnedTitle')}
          data-returned-banner
        >
          <Text size="sm">
            {t(returnedBy.office ? 'forms.record.returnedOffice' : 'forms.record.returnedText', {
              by: returnedBy.by,
              at: recordTime(returnedBy.at, timeZone),
            })}
          </Text>
          {returnedBy.reason ? (
            <Text size="sm" fw={600}>
              {t('forms.record.returnedReason', { reason: returnedBy.reason })}
            </Text>
          ) : null}
          {!office && (can.amend || can.resubmit) ? (
            <Text size="sm">{t('forms.record.returnedHint')}</Text>
          ) : null}
        </WarningAlert>
      ) : null}
      {superseded ? (
        <WarningAlert
          icon={<IconAlertTriangle size={18} />}
          className={classes.notice}
          title={t('forms.record.supersededTitle')}
        >
          <Text size="sm">
            {t('forms.record.supersededText', { revision: revisionText(record.revisionNo) })}
          </Text>
          <Button
            mt="xs"
            size="xs"
            variant="default"
            leftSection={<IconFilePlus size={14} />}
            loading={busy === 'reopen'}
            onClick={() => void reopenWithCurrent()}
          >
            {t('forms.record.reopenCurrent')}
          </Button>
        </WarningAlert>
      ) : null}
      <div className={classes.recordLayout}>
        <div className={classes.recordHeadArea}>
          <FormRecordHeader
            record={record}
            actions={
              coverActions || headerActions ? (
                <>
                  {coverActions}
                  {headerActions?.(record)}
                </>
              ) : null
            }
          />
        </div>
        <div className={classes.recordMain}>
          {!office && isDraft && can.editDraft && work ? (
            <RecordGuide
              fillMethod={record.fillMethod}
              files={draftFiles}
              busy={busy}
              officeOpen={officeOpen}
              {...(record.fillMethod === 'office'
                ? { onFillIn: () => void openInOffice(), onAttach: () => void attach() }
                : {})}
              {...(record.fillMethod === 'pdf_form'
                ? {
                    onFillInApp: () => setDialog('fill'),
                    fillInAppBlocked: pdfFillUrl
                      ? null
                      : formErrorText(t, { code: 'FILE_NOT_ON_BOARD' }),
                  }
                : {})}
              onUpload={() => {
                // Office'te doldurulmuş dosya ekliyse aynı form ikinci kez girmesin (ADR 0074)
                if (uploadNeedsConfirm(record.fillMethod, draftFiles)) setDialog('confirmUpload');
                else void pick('file');
              }}
              onDownloadTemplate={
                documents && templateFile
                  ? () =>
                      void run('template', () =>
                        documents.downloadFile({
                          fileId: templateFile.id,
                          name: templateFile.name,
                        }),
                      )
                  : undefined
              }
            />
          ) : null}
          <Panel title={t('forms.files.title')} className={classes.filesPanel}>
            {uploading ? (
              <div className={classes.uploadProgress}>
                <Text size="xs" className={classes.muted} role="status">
                  {uploadProgressText(t, uploading)}
                </Text>
                <Progress
                  value={uploadProgressPercent(uploading)}
                  size="sm"
                  aria-label={uploadProgressText(t, uploading)}
                />
              </div>
            ) : null}
            {isDraft && office ? (
              <p className={classes.muted}>{t('forms.files.draftOnVessel')}</p>
            ) : (
              <RecordFileList
                files={files}
                editable={isDraft && Boolean(can.editDraft) && !office}
                selected={preview?.sha256 ?? null}
                onSelect={(file) =>
                  setPreview({
                    sha256: file.sha256,
                    name: file.name,
                    size: file.size,
                    mimeType: file.mimeType,
                    ...('id' in file && file.id ? { id: file.id } : {}),
                    revisionNo: record.status.revisionNo,
                  })
                }
                onMove={(index, step) => void saveFiles(moveFile(draftFiles, index, step))}
                onRemove={(index) => void saveFiles(removeFile(draftFiles, index))}
                status={isDraft ? undefined : fileStatus}
                empty={t(isDraft ? 'forms.files.emptyDraft' : 'forms.files.none')}
              />
            )}
            {viewerFile && preview && previewAllowed ? (
              <div className={classes.viewerBox}>
                <FileViewer
                  ref={viewer}
                  key={`${preview.sha256}-${preview.revisionNo ?? 'draft'}`}
                  file={viewerFile}
                  url={source.fileUrl(record, preview, 'view')}
                  state="published"
                  canDownload={can.export}
                  canPrint={can.export && !isDraft}
                  onDownload={() =>
                    void source
                      .downloadFile(record, {
                        ...(preview.id ? { id: preview.id } : {}),
                        sha256: preview.sha256,
                        name: preview.name,
                      })
                      .catch(fail)
                  }
                  onPrint={() => {
                    const handle = viewer.current;
                    if (!handle || !printFooter) return;
                    void handle
                      .preparePrint(printFooter(record, preview.revisionNo))
                      .then(async (cleanup) => {
                        try {
                          await source.print();
                        } finally {
                          cleanup();
                        }
                      })
                      .catch(fail);
                  }}
                />
              </div>
            ) : preview && office ? (
              <p className={classes.muted}>{t('forms.files.notInOffice')}</p>
            ) : null}
          </Panel>
          <RevisionList
            record={record}
            timeZone={timeZone}
            selected={preview?.sha256 ?? null}
            onPreview={(file, revisionNo) =>
              setPreview({
                sha256: file.sha256,
                name: file.name,
                size: file.size,
                mimeType: file.mimeType,
                id: file.id,
                revisionNo,
              })
            }
          />
        </div>
        <aside className={classes.recordSide} aria-label={t('forms.info.title')}>
          <Panel title={t('forms.info.title')} className={docs.sideCard}>
            {facts}
          </Panel>
          {submitted || status === 'void' ? (
            <ApprovalPanel
              record={{
                status: status === 'pending' ? 'pending' : status,
                contentHash: record.pending?.contentHash ?? '',
                submissionId: record.pending?.submissionId ?? null,
                step: record.pending?.step ?? null,
                office: record.pending?.office ?? false,
              }}
              entry={null}
              title={t('forms.decision.title')}
              badge={<RecordBadges badges={badges.slice(0, 1)} />}
              canDecide={(can.decide || waiting) && status === 'pending'}
              blockedReason={
                waiting
                  ? t('forms.steps.waitingFor', { role: pendingRole })
                  : // Ofis adımı dosyalar bulutta doğrulanmadan verilemez (ADR 0042 §15) [sorular #240]
                    office &&
                      record.pending?.office &&
                      record.files.some((f) => f.officeState !== 'verified')
                    ? t('forms.errors.FILE_NOT_UPLOADED')
                    : null
              }
              canVoid={false}
              overdue={record.overdue}
              subjectName={t('forms.decision.subject', { record: record.recordNo })}
              errorText={(error) => formErrorText(t, error)}
              reauthKind="ism_form"
              onDecide={decide}
              onVoid={() => undefined}
              onCorrect={() => undefined}
            >
              {record.pending ? (
                <p className={classes.awaiting} data-overdue={record.overdue || undefined}>
                  {t('forms.revisions.awaiting', {
                    role: pendingRole,
                    since: recordTime(record.pending.since, timeZone),
                  })}
                </p>
              ) : null}
              {/* Ofiste karar düğmesi yoksa kararın nerede verildiği yazılır: ISM formu gemide
                  kaptanla kapanır, ofis yalnız görür (sahip kararı 2026-10-17; ADR 0073). Eski
                  ofis adımında adımın rolündeki kişi ofiste karar verir */}
              {office && record.pending && status === 'pending' && !can.decide && !waiting ? (
                <p className={classes.awaiting} role="note" data-decided-where>
                  {record.pending.office
                    ? t('forms.decision.officeStep', { role: pendingRole })
                    : t('forms.decision.onBoard', { role: pendingRole })}
                </p>
              ) : null}
              <ApprovalSteps record={record} timeZone={timeZone} />
            </ApprovalPanel>
          ) : null}
          {actions.length > 0 ? (
            <Panel title={t('documents.actions.title')} className={docs.sideCard}>
              <div className={docs.actionList}>{actions}</div>
            </Panel>
          ) : null}
        </aside>
      </div>

      {isDraft && !office && can.editDraft ? (
        <form
          className={classes.draftBar}
          noValidate
          onSubmit={draftForm.handleSubmit(
            (values) =>
              void run('save', async () => {
                await ship?.updateDraft(record.id, {
                  expectedRowVersion: record.draft?.rowVersion ?? 0,
                  title: values.title === '' ? null : values.title,
                  note: values.note === '' ? null : values.note,
                });
                setNotice({ tone: 'success', text: t('forms.record.saved') });
              }),
          )}
        >
          <TextInput
            label={t('forms.record.title')}
            className={classes.draftTitle}
            maxLength={400}
            {...draftForm.register('title')}
          />
          <Textarea
            label={t('forms.record.note')}
            className={classes.draftNote}
            autosize
            minRows={1}
            maxRows={4}
            maxLength={4000}
            {...draftForm.register('note')}
          />
          <Group gap="sm" className={classes.draftButtons}>
            <Button
              type="submit"
              variant="default"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={busy === 'save'}
            >
              {t('forms.record.saveDraft')}
            </Button>
            {can.submit ? (
              <Button
                leftSection={<IconSend size={16} />}
                disabled={draftFiles.length === 0 || draftForm.formState.isDirty}
                onClick={() => setDialog('submit')}
              >
                {t('forms.record.submit')}
              </Button>
            ) : null}
          </Group>
        </form>
      ) : null}

      <PmsPasswordDialog
        opened={dialog === 'submit'}
        title={t('forms.submit.title', { record: record.recordNo })}
        reauthKind="ism_form"
        body={t('forms.submit.body', { record: record.recordNo, count: draftFiles.length })}
        reason="none"
        confirmLabel={t('forms.record.submit')}
        errorText={(error) => formErrorText(t, error)}
        onClose={() => setDialog(null)}
        onConfirm={async ({ password }) => {
          const detail = await ship?.submit(record.id, password ? { password } : {});
          setDialog(null);
          if (detail) setState({ kind: 'ready', record: detail });
          setNotice({ tone: 'success', text: t('forms.record.submitted') });
          // Çalışma kopyası gönderilince kalkar (§18 madde 6)
          void work?.release(record.id).catch(() => undefined);
          return null;
        }}
      />
      <PmsPasswordDialog
        opened={dialog === 'resubmit'}
        title={t('forms.resubmit.title')}
        reauthKind="ism_form"
        body={t('forms.resubmit.body', { record: record.recordNo })}
        reason="required"
        reasonLabel={t('forms.resubmit.reason')}
        reasonMaxLength={2000}
        confirmLabel={t('forms.record.resubmit')}
        errorText={(error) => formErrorText(t, error)}
        onClose={() => setDialog(null)}
        onConfirm={async ({ password, reason }) => {
          const detail = await ship?.resubmit(record.id, {
            ...(password ? { password } : {}),
            reason,
          });
          setDialog(null);
          if (detail) setState({ kind: 'ready', record: detail });
          return null;
        }}
      />
      <PmsPasswordDialog
        opened={dialog === 'void'}
        title={t('forms.void.title', { record: record.recordNo })}
        reauthKind="ism_form"
        body={t('forms.void.body')}
        reason="required"
        reasonLabel={t('forms.void.reason')}
        note={t('forms.void.note')}
        reasonMaxLength={2000}
        confirmLabel={t('forms.record.void')}
        danger
        errorText={(error) => formErrorText(t, error)}
        onClose={() => setDialog(null)}
        onConfirm={async ({ password, reason }) => {
          const detail = await ship?.void(record.id, { ...(password ? { password } : {}), reason });
          setDialog(null);
          if (detail) setState({ kind: 'ready', record: detail });
          return null;
        }}
      />
      <ReasonDialog
        opened={dialog === 'discard'}
        title={t('forms.discard.title', { record: record.recordNo })}
        body={t('forms.discard.body')}
        confirmLabel={t('forms.record.discard')}
        danger
        onClose={() => setDialog(null)}
        onConfirm={async (reason) => {
          const detail = await ship?.discard(record.id, { reason });
          setDialog(null);
          if (detail) setState({ kind: 'ready', record: detail });
          void work?.release(record.id).catch(() => undefined);
        }}
      />
      <Modal
        opened={dialog === 'confirmUpload'}
        onClose={() => setDialog(null)}
        title={t('forms.howTo.confirmUploadTitle')}
        closeButtonProps={{ 'aria-label': t('forms.dialog.back') }}
      >
        <Text size="sm">{t('forms.howTo.confirmUploadBody')}</Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDialog(null)} data-autofocus>
            {t('forms.dialog.back')}
          </Button>
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={() => {
              setDialog(null);
              void pick('file');
            }}
          >
            {t('forms.howTo.confirmUpload')}
          </Button>
        </Group>
      </Modal>
      <Modal
        opened={dialog === 'stillOpen'}
        onClose={() => setDialog(null)}
        title={t('forms.record.stillOpenTitle')}
        closeButtonProps={{ 'aria-label': t('forms.dialog.back') }}
      >
        <Text size="sm">{t('forms.errors.FILE_STILL_OPEN')}</Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDialog(null)}>
            {t('forms.dialog.back')}
          </Button>
          <Button
            leftSection={<IconPaperclip size={16} />}
            onClick={() => {
              setDialog(null);
              void attach(true);
            }}
          >
            {t('forms.record.attachAnyway')}
          </Button>
        </Group>
      </Modal>
      {submitted && can.amend && ship ? (
        <AmendDrawer
          opened={dialog === 'amend'}
          record={record}
          work={work}
          onClose={() => setDialog(null)}
          onAmend={async (input: AmendInput) => {
            const detail = await ship.amend(record.id, input);
            setDialog(null);
            setState({ kind: 'ready', record: detail });
            setPreview(firstPreview(detail));
            setNotice({ tone: 'success', text: t('forms.record.amended') });
          }}
        />
      ) : null}
      {isDraft && work && ship ? (
        <PdfFormFiller
          opened={dialog === 'fill'}
          url={pdfFillUrl}
          title={t('forms.fill.title', { record: record.recordNo })}
          onClose={() => setDialog(null)}
          onSave={async (bytes) => {
            const detail = await attachFilledPdf({
              record,
              bytes,
              templateName: templateFile?.name ?? 'form.pdf',
              upload: (name, data) => work.uploadBytes(name, data),
              updateDraft: (id, patch) => ship.updateDraft(id, patch),
            });
            setDialog(null);
            setState({ kind: 'ready', record: detail });
            setNotice({ tone: 'success', text: t('forms.fill.saved') });
          }}
        />
      ) : null}
    </div>
  );
}
